import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type {
  ProfitAndLossReportPayload,
  ProfitAndLossRow,
  ProfitAndLossSection,
  ProfitAndLossSummaryRow,
} from "src/api/reports/profit-and-loss";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { useTranslation } from "react-i18next";

const formatAmount = (value: number): string => {
  if (value === 0) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value: number): string => {
  if (value === 0) return "—";
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

type Props = {
  data: ProfitAndLossReportPayload | undefined;
  isLoading: boolean;
};

const ProfitAndLossTable: React.FC<Props> = ({ data, isLoading }) => {
  const { t: generalT } = useTranslation("general");

  const columns = data?.columns ?? [];
  const sections = data?.sections ?? [];
  const summary = data?.summary ?? [];
  const totalColSpan = columns.length + 1;
  const isLastCol = (colIdx: number) => colIdx === columns.length - 1;

  const renderAmountCells = (
    amounts: number[],
    isBold: boolean,
    rowType: string,
  ) =>
    columns.map((col, colIdx) => {
      const val = amounts[colIdx] ?? 0;
      const isTotal = isLastCol(colIdx);
      const formatted =
        rowType === "percent" ? formatPercent(val) : formatAmount(val);

      return (
        <TableCell
          key={col.branch_id ?? "total"}
          align="right"
          sx={{
            fontWeight: isBold ? 700 : 400,
            color: isTotal ? "#101828" : "#344054",
            fontSize: "0.8125rem",
          }}
        >
          {formatted}
        </TableCell>
      );
    });

  const renderSectionHeader = (section: ProfitAndLossSection) => (
    <TableRow key={`section-${section.key}`}>
      <TableCell
        colSpan={totalColSpan}
        sx={{
          bgcolor: "primary.light",
          py: 1.25,
          px: 2,
        }}
      >
        <Typography
          fontWeight={700}
          fontSize="0.8125rem"
          color="primary.dark"
          textTransform="uppercase"
          letterSpacing="0.03em"
        >
          {section.title}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const renderRow = (row: ProfitAndLossRow) => (
    <TableRow key={row.key} hover>
      <TableCell
        sx={{
          fontWeight: row.is_bold ? 700 : 400,
          fontSize: "0.875rem",
          pl: 2,
        }}
      >
        {row.label}
      </TableCell>
      {renderAmountCells(row.amounts, row.is_bold, row.row_type)}
    </TableRow>
  );

  const renderSummaryRow = (row: ProfitAndLossSummaryRow) => (
    <TableRow key={row.key} hover>
      <TableCell
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          pl: 2,
        }}
      >
        {row.label}
      </TableCell>
      {renderAmountCells(row.amounts, true, row.row_type)}
    </TableRow>
  );

  const headerLabels = [
    generalT("account"),
    ...columns.map((col) => col.name),
  ];

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {headerLabels.map((label, idx) => (
            <TableCell key={label} align={idx === 0 ? "left" : "right"}>
              <Typography
                fontSize="12px"
                textTransform="uppercase"
                color="#fff"
                lineHeight="18px"
                fontWeight={500}
              >
                {label}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton
          rowsLength={8}
          cellsLength={headerLabels.length}
        />
      ) : (
        <TableBody>
          {sections.map((section) => [
            renderSectionHeader(section),
            ...section.rows.map(renderRow),
          ])}
          {summary.length > 0 && (
            <TableRow key="summary-header">
              <TableCell
                colSpan={totalColSpan}
                sx={{
                  bgcolor: "primary.light",
                  py: 1.25,
                  px: 2,
                }}
              >
                <Typography
                  fontWeight={700}
                  fontSize="0.8125rem"
                  color="primary.dark"
                  textTransform="uppercase"
                  letterSpacing="0.03em"
                >
                  {generalT("summary")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {summary.map(renderSummaryRow)}
        </TableBody>
      )}
    </Table>
  );
};

export default ProfitAndLossTable;
